import { useEffect, useState } from 'react';
import { apiUrl } from '../../../api/apiUrl';
import http, { authHeaders } from '../../../api/axios';
import { FileUpload } from '../../../components/files/FileUpload';
import InvoicePaperView from '../../../components/invoice/InvoicePaperView';
import { DocumentType, DocumentTypeTranslated } from '../../../types/Invoice';
import { useTranslation } from '../../../context/translationContext';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-toastify';

interface Props {
  companyId: number;
}
/**
 * Page component for managing logo of current company with template document view
 */
export const CompanyLogoPage = ({ companyId }: Props) => {
  const placeholderImg = `/img/placeholder-image.png`;
  const [imgSrc, setImgSrc] = useState<string>(placeholderImg);
  const { t } = useTranslation();
  /**
   * Loads company logo
   */
  const fetchData = async () => {
    if (!companyId) return;
    await http
      .apiGetFiles({
        url: `${apiUrl.COMPANY_LOGO_ITEM}?companyId=${companyId}`,
        config: {
          headers: {
            ...authHeaders(),
            'Content-Type': 'application/json',
          },
          method: 'GET',
          responseType: 'blob',
        },
      })
      .then(async (response) => {
        if (response?.statusText === 'No Content') return setImgSrc(placeholderImg);
        const fileUrl: string = URL.createObjectURL(response?.data);
        setImgSrc(fileUrl);
      });
  };

  useEffect(() => {
    fetchData();
  }, [companyId]);

  /**
   * Placeholder content
   */
  const placeholder = () => {
    return (
      <div role="status" className="space-y-2.5 w-full">
        <div className="flex items-center w-full space-x-2 px-1">
          <div className="h-2.5 bg-gray-200 rounded-full dark:bg-gray-700 w-32"></div>
          <div className="h-2.5 bg-gray-300 rounded-full dark:bg-gray-600 w-24"></div>
          <div className="h-2.5 bg-gray-300 rounded-full dark:bg-gray-600 w-full"></div>
        </div>
        <div className="flex items-center w-full space-x-2 px-1">
          <div className="h-2.5 bg-gray-200 rounded-full dark:bg-gray-700 w-full"></div>
          <div className="h-2.5 bg-gray-300 rounded-full dark:bg-gray-600 w-full"></div>
          <div className="h-2.5 bg-gray-300 rounded-full dark:bg-gray-600 w-24"></div>
        </div>
        <div className="flex items-center w-full space-x-2 px-1">
          <div className="h-2.5 bg-gray-300 rounded-full dark:bg-gray-600 w-full"></div>
          <div className="h-2.5 bg-gray-200 rounded-full dark:bg-gray-700 w-80"></div>
          <div className="h-2.5 bg-gray-300 rounded-full dark:bg-gray-600 w-full"></div>
        </div>
        <div className="flex items-center w-full space-x-2 px-1">
          <div className="h-2.5 bg-gray-200 rounded-full dark:bg-gray-700 w-full"></div>
          <div className="h-2.5 bg-gray-300 rounded-full dark:bg-gray-600 w-full"></div>
          <div className="h-2.5 bg-gray-300 rounded-full dark:bg-gray-600 w-24"></div>
        </div>
        <div className="flex items-center w-full space-x-2 px-1">
          <div className="h-2.5 bg-gray-300 rounded-full dark:bg-gray-600 w-32"></div>
          <div className="h-2.5 bg-gray-300 rounded-full dark:bg-gray-600 w-24"></div>
          <div className="h-2.5 bg-gray-200 rounded-full dark:bg-gray-700 w-full"></div>
        </div>
      </div>
    );
  };

  /**
   * Removes logo from company
   */
  const handleRemoveLogo = async () => {
    if (!imgSrc) return;
    const response = await http.apiDelete({
      url: `${apiUrl.COMPANY_LOGO_DELETE}?companyId=${companyId}`,
    });
    if (response?.status === 204) {
      toast.success('Success!');
      fetchData();
    }
  };

  return (
    <>
      <div className="flex justify-between">
        <div className="m-auto">
          <div className="flex flex-row justify-between overflow-x-hidden text-xs">
            <InvoicePaperView>
              <div className={`p-3 relative`}>
                {/* body */}
                <div className="relative flex flex-col">
                  <div className="flex justify-between max-h-[120px]">
                    {/* --- logo --- */}
                    <div className="relative max-w-7/12 min-w-[50%] mr-5">
                      <div className="relative flex">
                        <img
                          src={imgSrc}
                          alt="Company logo"
                          className={`relative max-w-[250px] max-h-[120px]`}
                        />
                        {imgSrc !== placeholderImg && (
                          <div>
                            <div className="border bg-red-500 h-auto rounded-full hover:bg-red-800 hover:text-white px-1 py-1 focus:outline-none z-30">
                              <XMarkIcon
                                className="cursor-pointer w-4 h-4 text-white"
                                onClick={handleRemoveLogo}
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                    {/* --- document type --- */}
                    <div className="w-full max-h-[120px]">
                      <div className="font-bold">
                        <div className="inline-flex items-center text-lg">
                          {/* document type */}
                          {t(DocumentTypeTranslated[DocumentType.INVOICE])}
                          {/* document type - edit dropdown */}
                        </div>
                      </div>
                      <div className="center w-full">{placeholder()}</div>
                    </div>
                  </div>
                  <hr className="mt-2" />
                  <div className="grid grid-rows-2 grid-flow-col gap-4">
                    <div className="row-span-2">
                      {/* --- supplier --- */}
                      <div className="col-span-2">
                        <div className="font-bold text-lg">{t('page.invoice.supplier')}</div>
                        <div className="border-black border border-1 p-2">{placeholder()}</div>
                      </div>
                      {/* Payment info */}
                      <div className="row-span-2 col-span-2">
                        <div className="font-bold text-lg">{t('page.invoice.payment-info')}</div>
                        <div className="border-black border border-1 px-1 py-1">
                          {placeholder()}
                        </div>
                      </div>
                    </div>
                    {/* --- subscriber --- */}
                    <div className="row-span-2">
                      <div className="font-bold text-lg">{t('page.invoice.subscriber')}</div>
                      <div className="border-black border border-1 p-2">{placeholder()}</div>
                    </div>
                  </div>
                  {/* Invoice items */}
                  <div className="mt-2">
                    <div>{t('page.invoice.invoice-items-headline')}</div>
                    <div>
                      <div>{placeholder()}</div>
                    </div>
                  </div>
                  <div className="w-full mx-auto flex justify-between mt-2 p-3">
                    <div className="w-7/12">
                      {/* Note */}
                      <div className="mr-5">
                        <div className="flex flex-col">
                          <span className="font-bold">{t('page.invoice.note')}</span>
                          <div>
                            <div>{placeholder()}</div>
                          </div>
                        </div>
                      </div>
                    </div>
                    {/* Summary */}
                    <div className="p-2 w-5/12 border">{placeholder()}</div>
                  </div>
                  <div className="center w-full">
                    {/* VAT total */}
                    <div className="w-[70%]">
                      <span className="pr-2 font-bold">
                        {t('page.invoice.vat-summary-headline')} {'( CZK )'}
                      </span>
                      {placeholder()}
                    </div>
                  </div>
                </div>
              </div>
            </InvoicePaperView>
          </div>
        </div>
        <div className="w-2/3">
          <FileUpload
            apiPostFilesUrl={`${apiUrl.COMPANY_LOGO_UPLOAD}?companyId=${companyId}`}
            onUpload={() => fetchData()}
          />
        </div>
      </div>
    </>
  );
};
